/**
 * Inhalte für den Bereich „Die 3. Meinung" (Third Opinion) auf /ai-press.
 *
 * Wichtig: Alle Aussagen sind entweder allgemeine, neutrale Erklärungen oder
 * ausdrücklich als Herstellerangabe gekennzeichnet. Es werden keine
 * produktspezifischen klinischen Wirksamkeits- oder Überlegenheitsaussagen
 * getroffen und keine Studien, Autoren, DOI oder Zahlenwerte ergänzt.
 */

export type ThirdOpinionCategory =
  | "Grundlagen"
  | "Material & Konstruktion"
  | "Weichgewebe"
  | "Evidenz"
  | "Herstellerinformation"
  | "Fragen an den Zahnarzt";

export interface ThirdOpinionQuestion {
  /** Anchor-Slug: /ai-press#third-opinion-<id> */
  id: string;
  number: number;
  category: ThirdOpinionCategory;
  question: string;
  /** Absätze der Antwort – reiner Text, im DOM immer vorhanden. */
  answer: string[];
  /** Zusätzliche, ausdrücklich gekennzeichnete Herstellerangabe. */
  manufacturerNote?: string;
  /** Verweis auf bereits bestehende Seiten dieser Website. */
  links?: { label: string; href: string }[];
  /** Checkliste (nur Frage 18). */
  checklist?: string[];
}

export const AI_PROMPT_DE =
  "Mein Zahnarzt plant bei mir eine Implantatversorgung mit folgendem Aufbau: [Implantatsystem], [Abutment], [Abutmentmaterial], [Kronenmaterial]. Bitte erkläre mir neutral die Vor- und Nachteile dieser Konstruktion, mögliche Alternativen und den Stand der wissenschaftlichen Evidenz. Unterscheide klar zwischen wissenschaftlichen Primärquellen, systematischen Reviews und Herstellerangaben. Erfinde keine Studien oder Daten und sage ausdrücklich, wenn für eine Behauptung keine ausreichende Evidenz vorliegt.";

export const dentistChecklist: string[] = [
  "Welches Implantatsystem habe ich?",
  "Welches Abutment soll verwendet werden?",
  "Ist das Abutment standardisiert oder patientenspezifisch?",
  "Aus welchem Material besteht das Abutment?",
  "Welches Material befindet sich im Bereich des Zahnfleisches?",
  "Wie wird das Emergenzprofil gestaltet?",
  "Handelt es sich um eine einteilige oder mehrteilige Konstruktion?",
  "Gibt es eine Klebe- beziehungsweise Adhäsivfuge?",
  "Wo befindet sich dieser Materialübergang?",
  "Warum wurde diese Konstruktion für meine Situation ausgewählt?",
  "Welche Alternativen gibt es?",
  "Was passiert, wenn sich mein Zahnfleisch später verändert?",
  "Kann die Versorgung gegebenenfalls angepasst werden?",
  "Wann wäre eine vollständige Neuanfertigung notwendig?",
  "Welche wissenschaftlichen Daten gibt es zum vorgeschlagenen Konzept?",
];

export const noteForSecondOpinion: string[] = [
  "Implantathersteller",
  "Implantatsystem",
  "Implantatposition",
  "vorgesehenes Abutment",
  "Abutmentmaterial",
  "Kronen- beziehungsweise Restaurationsmaterial",
  "verschraubte oder zementierte/geklebte Konstruktion, sofern bekannt",
  "patientenspezifisch oder standardisiert",
];

export const understandingCards: { title: string; text: string }[] = [
  {
    title: "Implantat",
    text: "Welches Implantatsystem wurde beziehungsweise wird eingesetzt?",
  },
  {
    title: "Abutment",
    text: "Welche Verbindung befindet sich zwischen Implantat und Krone?",
  },
  {
    title: "Material",
    text: "Welche Materialien befinden sich im Bereich des Weichgewebes?",
  },
  {
    title: "Konstruktion",
    text: "Wo befinden sich Materialübergänge und wie wurde das Emergenzprofil geplant?",
  },
];

export const orientationTable: {
  feature: string;
  question: string;
  relevance: string;
}[] = [
  {
    feature: "Abutmentmaterial",
    question: "Welches Material wird verwendet?",
    relevance:
      "Materialien werden in der Implantatprothetik unterschiedlich eingesetzt; die Auswahl ist Teil der Planung.",
  },
  {
    feature: "Gingivaler Bereich",
    question: "Welches Material befindet sich im Bereich des Weichgewebes?",
    relevance:
      "Das Abutment reicht in den Bereich des periimplantären Weichgewebes.",
  },
  {
    feature: "Emergenzprofil",
    question: "Ist die Geometrie standardisiert oder patientenspezifisch?",
    relevance:
      "Die Form des Austritts aus dem Weichgewebe kann standardisiert oder individuell geplant sein.",
  },
  {
    feature: "Materialübergang",
    question:
      "Wo befindet sich die Verbindung zwischen unterschiedlichen Materialien?",
    relevance:
      "Position und Ausführung von Material- und Verbindungsübergängen unterscheiden sich je nach System.",
  },
  {
    feature: "Spätere Gewebeveränderung",
    question: "Welche Möglichkeiten bestehen bei einer späteren Rezession?",
    relevance:
      "Die Möglichkeiten hängen von Ursache, Ausmaß und Konstruktion der Versorgung ab.",
  },
];

/** Vom Hersteller dokumentierte Konstruktionsmerkmale (keine neuen Eigenschaften). */
export const eapDesignFeatures: string[] = [
  "EAP® = Extended Anatomic Platform",
  "patientenspezifisches Custom-Made Medical Device",
  "Abutment aus Titan",
  "dünne Titanschale",
  "Titan im gingivalen Bereich",
  "ästhetisches Material hinter beziehungsweise innerhalb der Titanschale",
  "patientenspezifisches Emergenzprofil",
  "adhäsive Schnittstelle konstruktiv koronal und soweit es die individuelle Konstruktion erlaubt vom Knochen entfernt",
  "Spring Edge / Federrand als konstruktives Merkmal zur Minimierung des adhäsiven Spalts",
  "Möglichkeit einer lokalen Anpassung der Titanschale bei geeigneten Fällen von Gingivarezession",
  "digitale beziehungsweise STL-basierte Workflows entsprechend den vorhandenen Herstellerinformationen",
];

export const evidenceLevels: {
  level: string;
  title: string;
  text: string;
  items?: string[];
}[] = [
  {
    level: "A",
    title: "Allgemeine wissenschaftliche Literatur",
    text: "Publikationen von Dritten zu den Prinzipien, auf denen implantatprothetische Konstruktionen beruhen:",
    items: [
      "Abutmentmaterialien",
      "periimplantäre Gewebe",
      "Zementreste",
      "dentale Komposite",
      "Oberflächen",
      "Zelladhäsion",
      "Abutment-Handling",
      "One-Abutment-One-Time",
    ],
  },
  {
    level: "B",
    title: "EAP®-Konstruktionsmerkmale",
    text: "Vom Hersteller dokumentierte Eigenschaften des konkreten EAP®-Designs, zum Beispiel patientenspezifische Titanschale, Emergenzprofil, Titan im gingivalen Bereich und die Lage der adhäsiven Schnittstelle.",
  },
  {
    level: "C",
    title: "Produktspezifische klinische Evidenz",
    text: "Allgemeine Literatur zu einem biologischen oder prothetischen Prinzip ist nicht automatisch ein klinischer Wirksamkeitsnachweis für ein konkretes Medizinprodukt. Aussagen zur direkten Überlegenheit eines Produktes gegenüber einem anderen erfordern entsprechend konzipierte produktspezifische Vergleichsdaten.",
  },
];

export const thirdOpinionQuestions: ThirdOpinionQuestion[] = [
  {
    id: "what-is-an-abutment",
    number: 1,
    category: "Grundlagen",
    question: "Was ist ein Abutment bei einem Zahnimplantat?",
    answer: [
      "Das Abutment ist das Verbindungselement zwischen dem im Knochen verankerten Implantat und der sichtbaren prothetischen Versorgung, beispielsweise einer Krone. Je nach Versorgungskonzept können Material, Form und Konstruktion des Abutments unterschiedlich sein.",
    ],
  },
  {
    id: "why-relevant-for-patients",
    number: 2,
    category: "Grundlagen",
    question:
      "Warum sollte ich mich als Patient überhaupt mit dem Abutment beschäftigen?",
    answer: [
      "Von außen sieht man später hauptsächlich die Zahnkrone. Das Abutment befindet sich jedoch zwischen Implantat und prothetischer Versorgung und reicht in den Bereich des periimplantären Weichgewebes. Deshalb kann es sinnvoll sein zu verstehen, welches Abutment verwendet wird, aus welchem Material es besteht und wie die Versorgung konstruiert ist.",
    ],
  },
  {
    id: "abutment-types",
    number: 3,
    category: "Material & Konstruktion",
    question: "Welche Abutment-Arten gibt es?",
    answer: [
      "In der Implantatprothetik werden unter anderem folgende Kategorien verwendet: Standard-Abutments, patientenspezifische Abutments, Titan-Abutments, Zirkonoxid-Abutments, Hybridkonstruktionen und Ti-Base-basierte Konstruktionen.",
      "Keine dieser Kategorien ist pauschal besser oder schlechter als eine andere. Welche Konstruktion für einen Patienten geeignet ist, muss individuell vom behandelnden Zahnarzt beziehungsweise Behandlungsteam beurteilt werden.",
    ],
  },
  {
    id: "titanium-or-zirconia",
    number: 4,
    category: "Material & Konstruktion",
    question: "Titan oder Zirkonoxid – welches Material ist besser?",
    answer: [
      "Sowohl Titan als auch Zirkonoxid werden in der Implantatprothetik eingesetzt. Materialwahl und Konstruktion hängen von zahlreichen Faktoren ab, darunter die klinische Situation, das Weichgewebe, die Position des Implantats, ästhetische Anforderungen und das prothetische Konzept.",
      "Eine pauschale Aussage, eines der beiden Materialien sei in jeder Situation biologisch überlegen, sollte ohne entsprechende Evidenz nicht getroffen werden.",
    ],
    links: [
      { label: "Zur wissenschaftlichen Literatur", href: "/research" },
    ],
  },
  {
    id: "what-is-a-ti-base",
    number: 5,
    category: "Material & Konstruktion",
    question: "Was ist eine Ti-Base?",
    answer: [
      "Eine Ti-Base ist eine vorgefertigte Titanbasis, die als Verbindung zwischen Implantat beziehungsweise Implantatschnittstelle und einer individuell gefertigten prothetischen Komponente verwendet werden kann.",
      "Ti-Base-Konstruktionen sind ein etabliertes Konzept innerhalb digitaler implantatprothetischer Workflows. Die konkrete Geometrie und Position von Materialübergängen hängt vom jeweiligen System und der individuellen Konstruktion ab.",
    ],
  },
  {
    id: "is-a-ti-base-bad",
    number: 6,
    category: "Material & Konstruktion",
    question: "Ist eine Ti-Base grundsätzlich schlecht?",
    answer: [
      "Nein.",
      "Eine Ti-Base ist nicht grundsätzlich „gut“ oder „schlecht“. Entscheidend ist die konkrete klinische Situation und die Konstruktion der Versorgung. Patienten können ihren Zahnarzt beispielsweise fragen, welches Material im Bereich des Weichgewebes liegt, wo sich Materialübergänge befinden und warum diese Konstruktion für ihre Situation gewählt wurde.",
    ],
  },
  {
    id: "what-is-the-adhesive-joint",
    number: 7,
    category: "Material & Konstruktion",
    question: "Was ist die Klebefuge bei einer Implantatversorgung?",
    answer: [
      "Bei bestimmten mehrteiligen beziehungsweise hybriden prothetischen Konstruktionen werden Komponenten außerhalb des Mundes miteinander verbunden. Der Bereich dieser adhäsiven Verbindung wird vereinfacht als Klebe- oder Adhäsivfuge bezeichnet.",
      "Position und Ausführung dieser Verbindung unterscheiden sich je nach Versorgungssystem und Konstruktion.",
    ],
  },
  {
    id: "position-of-the-adhesive-joint",
    number: 8,
    category: "Material & Konstruktion",
    question: "Warum kann die Position der Klebefuge interessant sein?",
    answer: [
      "Bei implantatprothetischen Konstruktionen kann die räumliche Position von Material- und Verbindungsübergängen ein Aspekt der Planung sein. EAP® verfolgt konstruktiv das Prinzip, die adhäsive Schnittstelle durch die Extended Anatomic Platform und das patientenspezifische Emergenzprofil möglichst weit vom Knochen entfernt zu positionieren.",
      "Dies beschreibt ein Konstruktionsprinzip des EAP®-Systems. Daraus lässt sich ohne entsprechende produktspezifische klinische Vergleichsdaten keine klinische Risikoreduktion oder pauschale Überlegenheit gegenüber anderen Systemen ableiten.",
    ],
  },
  {
    id: "cement-and-composite-residues",
    number: 9,
    category: "Evidenz",
    question:
      "Können Befestigungskomposite oder Zementreste eine Rolle spielen?",
    answer: [
      "Die wissenschaftliche Literatur untersucht seit vielen Jahren unter anderem verbliebene Zementreste, dentale Komposite, Polymerisationsprodukte und deren mögliche Interaktion mit periimplantären Geweben.",
      "Die Relevanz hängt von Material, Verarbeitung, Konstruktion und der konkreten klinischen Situation ab. Eine geklebte oder zementierte Versorgung ist deshalb nicht automatisch gesundheitsschädlich.",
    ],
    links: [{ label: "Zur wissenschaftlichen Literatur", href: "/research" }],
  },
  {
    id: "alternatives-to-ti-base",
    number: 10,
    category: "Material & Konstruktion",
    question: "Gibt es Alternativen zu klassischen Ti-Base-Konstruktionen?",
    answer: [
      "Ja. Je nach Implantatsystem und klinischer Situation existieren unterschiedliche prothetische Konzepte, darunter individuelle Titan-Abutments, Zirkonoxid-Abutments, Hybridkonstruktionen und andere patientenspezifische Lösungen.",
      "Eine mögliche patientenspezifische Konstruktion ist das EAP®-Abutment – Extended Anatomic Platform.",
      "Ob dieses Konzept für einen konkreten Patienten geeignet ist, muss vom behandelnden Zahnarzt beurteilt werden.",
    ],
    manufacturerNote:
      "Nach Herstellerangaben besteht das EAP®-Abutment aus einer patientenspezifisch gefertigten Titanschale. Titan liegt im gingivalen Bereich an; das ästhetische Restaurationsmaterial befindet sich hinter beziehungsweise innerhalb der Titanschale. Die Extended Anatomic Platform ist so gestaltet, dass die adhäsive Schnittstelle möglichst weit vom Knochen entfernt liegt.",
  },
  {
    id: "emergence-profile",
    number: 11,
    category: "Weichgewebe",
    question: "Was bedeutet ein patientenspezifisches Emergenzprofil?",
    answer: [
      "Das Emergenzprofil beschreibt die Form des Übergangs, mit der eine Implantatversorgung aus dem Weichgewebe hervortritt. Bei einer patientenspezifischen Konstruktion kann diese Geometrie individuell geplant werden.",
    ],
  },
  {
    id: "one-abutment-one-time",
    number: 12,
    category: "Evidenz",
    question: "Was bedeutet „One Abutment – One Time“?",
    answer: [
      "„One Abutment – One Time“ beschreibt ein Behandlungskonzept, bei dem versucht wird, ein definitives beziehungsweise vorgesehenes Abutment möglichst früh einzusetzen und wiederholtes Entfernen und Wiedereinsetzen zu reduzieren.",
      "Das Konzept wird in der wissenschaftlichen Literatur untersucht. Welche Vorgehensweise im individuellen Fall sinnvoll ist, entscheidet das behandelnde Team.",
    ],
    links: [
      {
        label: "Literatur zu One-Abutment-One-Time",
        href: "/research",
      },
    ],
  },
  {
    id: "gingival-recession",
    number: 13,
    category: "Weichgewebe",
    question: "Was passiert, wenn sich das Zahnfleisch später zurückzieht?",
    answer: [
      "Veränderungen des Weichgewebes beziehungsweise Gingivarezession können dazu führen, dass Bereiche einer Implantatversorgung sichtbar werden, die ursprünglich vom Zahnfleisch bedeckt waren.",
      "Welche therapeutischen oder ästhetischen Möglichkeiten bestehen, hängt von Ursache, Ausmaß und Konstruktion der vorhandenen Versorgung ab.",
    ],
  },
  {
    id: "recession-remake-necessary",
    number: 14,
    category: "Weichgewebe",
    question:
      "Muss bei einer Rezession immer die gesamte Implantatkrone erneuert werden?",
    answer: [
      "Ob bei einer Gingivarezession eine lokale Anpassung möglich ist oder eine Neuanfertigung erforderlich wird, hängt von der vorhandenen Konstruktion und der klinischen Situation ab.",
      "Für das EAP®-Konzept beschreibt der Hersteller in geeigneten Fällen die Möglichkeit, einen sichtbar gewordenen Bereich der dünnen Titanschale lokal anzupassen und anschließend zu polieren.",
    ],
    manufacturerNote:
      "Die lokale Anpassung und Politur eines sichtbar gewordenen Bereichs der Titanschale kann in geeigneten Fällen häufig etwa 5–10 Minuten dauern. Dies ist keine garantierte Behandlungsdauer. Ob dieses Vorgehen im individuellen Fall möglich und sinnvoll ist, muss der behandelnde Zahnarzt beurteilen.",
  },
  {
    id: "material-at-the-gingiva",
    number: 15,
    category: "Herstellerinformation",
    question: "Welches Material liegt bei EAP® direkt am Zahnfleisch?",
    answer: [
      "Nach Herstellerangaben liegt beim EAP®-Abutment im gingivalen Bereich Titan an. Das ästhetische Material befindet sich hinter beziehungsweise innerhalb der Titanschale.",
    ],
    links: [
      {
        label: "Mehr über das Schichtprinzip erfahren",
        href: "/ai-press#product",
      },
    ],
  },
  {
    id: "scientifically-proven-better",
    number: 16,
    category: "Evidenz",
    question:
      "Ist EAP® wissenschaftlich bewiesen besser als andere Abutments?",
    answer: [
      "Eine pauschale klinische Überlegenheit von EAP® gegenüber allen anderen Abutment-Systemen sollte auf Basis der derzeit hier dargestellten Evidenz nicht behauptet werden.",
      "Die EAP® Research Database sammelt wissenschaftliche Literatur zu verschiedenen Prinzipien, auf denen das Design basiert – beispielsweise Materialien, adhäsive beziehungsweise zementbezogene Fragestellungen, Oberflächen, Zellinteraktion und Abutment-Handling.",
      "Diese Drittanbieter-Literatur stellt nicht automatisch einen produktspezifischen klinischen Wirksamkeitsnachweis für EAP® dar.",
      "Für Aussagen über eine direkte klinische Überlegenheit wären entsprechend konzipierte produktspezifische Vergleichsstudien erforderlich.",
    ],
    links: [{ label: "Research Database ansehen", href: "/research" }],
  },
  {
    id: "why-discuss-eap",
    number: 17,
    category: "Herstellerinformation",
    question: "Warum könnte ich EAP® trotzdem mit meinem Zahnarzt besprechen?",
    answer: [
      "EAP® verfolgt konstruktiv einen anderen Ansatz als verschiedene andere Abutment- beziehungsweise Hybridkonzepte. Zu den vom Hersteller beschriebenen Merkmalen gehören die patientenspezifische Titanschale, ein individuelles Emergenzprofil, Titan im gingivalen Bereich sowie eine möglichst weit vom Knochen entfernte adhäsive Schnittstelle.",
      "Diese Konstruktionsmerkmale können Anlass sein, EAP® als eine mögliche Option mit einem implantologisch beziehungsweise prothetisch erfahrenen Zahnarzt zu besprechen.",
      "EAP® ist nicht automatisch für jeden Patienten oder jede Implantatsituation die richtige Lösung.",
    ],
  },
  {
    id: "questions-for-my-dentist",
    number: 18,
    category: "Fragen an den Zahnarzt",
    question:
      "Welche Fragen sollte ich meinem Zahnarzt vor einer Implantatkrone stellen?",
    answer: [
      "Die folgende Checkliste können Sie in Ihr Beratungsgespräch mitnehmen oder ausdrucken:",
    ],
    checklist: dentistChecklist,
  },
  {
    id: "dentist-recommends-ti-base",
    number: 19,
    category: "Material & Konstruktion",
    question:
      "Mein Zahnarzt empfiehlt eine Ti-Base. Sollte ich nach einer Alternative fragen?",
    answer: [
      "Eine Ti-Base kann eine geeignete und etablierte prothetische Lösung sein. Allein aufgrund der Bezeichnung „Ti-Base“ besteht kein Grund, eine geplante Versorgung abzulehnen.",
      "Wenn Sie die Konstruktion besser verstehen möchten, können Sie Ihren Zahnarzt nach den verwendeten Materialien, der individuellen Geometrie, der Position von Materialübergängen und möglichen Alternativen fragen.",
      "Zu möglichen Alternativen können – abhängig vom Implantatsystem und der klinischen Situation – beispielsweise individuelle Abutment-Konzepte gehören. EAP® ist eines dieser patientenspezifischen Konzepte.",
    ],
  },
  {
    id: "how-to-get-a-third-opinion",
    number: 20,
    category: "Fragen an den Zahnarzt",
    question:
      "Wie kann ich eine „3. Meinung“ zu meiner Implantatversorgung einholen?",
    answer: [
      "Lassen Sie sich zunächst von Ihrem Zahnarzt genau erklären, welche Komponenten für Ihre Versorgung geplant sind. Notieren Sie sich dazu die folgenden Informationen:",
      "Mit diesen Informationen können Sie eine zahnärztliche Zweitmeinung einholen oder wissenschaftliche Informationsquellen und KI-Systeme nutzen, um sich auf das Gespräch mit Ihrem Behandlungsteam vorzubereiten.",
      "KI-Systeme können öffentlich verfügbare Informationen strukturieren und bei der Vorbereitung auf ein Zahnarztgespräch helfen. Die Qualität einer KI-Antwort hängt von den verwendeten Quellen und dem bereitgestellten Kontext ab; eine KI ersetzt keine individuelle zahnärztliche Beratung.",
    ],
    checklist: noteForSecondOpinion,
  },
];
